import {
  ContractInfo,
  HotTokensRowInfo,
  PoolInfo,
} from 'api/types/httpsTypes/contracts';
import { useQueryParams } from 'modules/routing/useQueryParams';
import React from 'react';
import { ResizableBox, ResizeCallbackData } from 'react-resizable';
import { DexChart } from '../../../components/Chart/DexChart';
import GeckoTerminalPlaceHolder from '../../../../../assets/img/token/mockup-geckoterminal.png';
import {
  EllipsisHorizontalIcon,
  PauseCircleIcon,
  PuzzlePieceIcon,
  ViewColumnsIcon,
} from '@heroicons/react/24/outline';
import { SwapsTerminal } from '../../stats/swaps';
import * as yup from 'yup';
import { Chains } from 'api/types/httpsTypes/d-wallets';
import { CallsInfo } from '../../stats/callInfo';
import { twMerge } from 'tailwind-merge';
import createPersistedState from 'use-persisted-state';
import { HoldersComponent } from '../../stats/holders';
import { useQuery } from '@tanstack/react-query';
import {
  ContractService,
  contractServiceKeys,
} from 'api/services/httpServices/ContractService';
import { duration } from 'moment';
import { formatNumberWithSuffix, safeBig } from 'utils/FormatNumber';
import Button from 'modules/shared-components/button/button';
import { OpenTradesComponent } from '../../../dashboard/components/trades/openTrades';
import { HistoryTradesComponent } from '../../../dashboard/components/trades/historyTrades';
import { OpenTradesFilterProvider } from '../../../dashboard/components/trades/usePageParams';
import { screenGte, useMediaQuery } from 'modules/media/use-media-query';
import { OpenTradesFiltersBar } from '../../../dashboard/components/trades/OpenTradesFilters';
import { SwapsFilterBar } from '../../stats/SwapsFilters';
import {
  useContractInfo,
  useHistoryTradesSettings,
} from 'modules/routing/mainSettingsProvider';
import { TokenNameHeader } from './TokenCardHeader';
import { FilterState, useSwapsFilters } from '../../stats/swapsFilterProvider';
import Checkbox from '../../../components/inputs/Checkbox';

export enum AnalyticsKeys {
  Security = 'security',
  Info = 'info',
  Trade = 'trade',
  Trades = 'trades',
  History = 'history',
  SmartSwaps = 'swaps',
  MainStats = 'main-stats',
  SmartStats = 'smart-stats',
  TelegramCalls = 'telegram-calls',
  Holders = 'holders',
}

export function useSchemaPage() {
  const isDesktop = useMediaQuery(screenGte.large);
  return {
    activeAnalytics: yup
      .string()
      .default(isDesktop ? AnalyticsKeys.Trades : undefined)
      .oneOf(Object.values(AnalyticsKeys)),
    maker: yup.string().nullable(),
    minTime: yup.number().nullable(),
    maxTime: yup.number().nullable(),
  };
}

const chartHeightLocalStorage = `chartHeight`;
export const useChartHeight = createPersistedState<number>(
  chartHeightLocalStorage
);

export interface SwapsChart {
  price: number;
  timestamp: string;
}

export function TokenInfoTradeBody({
  contract,
  selectedPool,
  hotTokens,
}: {
  contract: ContractInfo;
  selectedPool: PoolInfo | undefined;
  hotTokens: HotTokensRowInfo[] | undefined;
}) {
  const [queryParams, setQueryParams] = useQueryParams(useSchemaPage());
  const [chartHeight, setChartHeight] = useChartHeight(450);
  const { isSwapCompact, setIsSwapCompact } = useContractInfo();
  const { paused, setFilters, filtersState: filters } = useSwapsFilters();
  const updateFilters = (update: Partial<FilterState>) => {
    setFilters({ ...filters, ...update });
  };
  const { isFailedTradesHistoryHidden, setIsHistoryTradesFailedHidden } =
    useHistoryTradesSettings();

  const handleResize = (_e: React.SyntheticEvent, data: ResizeCallbackData) => {
    const newHeight = data.size.height;
    setChartHeight(newHeight);
    document
      .querySelector('.dex-chart-wrapper')
      ?.classList.remove('pointer-events-none');
  };

  const params = {
    tokenAddress: contract.generalInfo.address,
  };

  const {
    data: mainData,
    error,
    isLoading,
  } = useQuery({
    queryKey: contractServiceKeys.getHolders(params),
    queryFn: () => ContractService.getHolders(params),
    keepPreviousData: true,
    enabled: contract.generalInfo.chain.key === Chains.Solana,
    refetchInterval: duration(12, 'seconds').asMilliseconds(),
  });

  const AnalyticsButton = ({
    label,
    keyValue,
    isVisible = true,
    extraContent,
  }: {
    label: string;
    keyValue: AnalyticsKeys;
    isVisible?: boolean;
    extraContent?: any;
  }) => {
    if (!isVisible) return null;

    const isActive = queryParams.activeAnalytics === keyValue;

    return (
      <Button
        type="button"
        onClick={() => {
          setQueryParams({
            // ✅ keep maker if it exists
            ...(queryParams.maker ? { maker: queryParams.maker } : {}),
            ...(queryParams.minTime ? { minTime: queryParams.minTime } : {}),
            ...(queryParams.maxTime ? { maxTime: queryParams.maxTime } : {}),
            // ✅ always set or clear activeAnalytics
            activeAnalytics: keyValue || undefined,
          });
        }}
        className={twMerge(
          'xxs:p-2 xxs:rounded-none xxs:border-solid border-transparent border-x-0 border-t-0',
          isActive
            ? 'xxs:text-dex-white xxs:border-solid border-white'
            : 'text-dex-white-secondary hover:text-dex-white'
        )}
      >
        <div className="flex space-x-1 items-center justify-center">
          <div className="normal-case xxs:pt-1">
            {label}
            {extraContent}
          </div>
        </div>
      </Button>
    );
  };

  return (
    <div>
      <ResizableBox
        height={chartHeight}
        width={Infinity}
        axis="y"
        handle={
          <div
            className=" hidden lg:flex justify-center items-center
                 bg-dex-black-700 hover:bg-dex-black-600 h-1 w-full cursor-row-resize
                 text-dex-white-secondary hover:text-dex-white transition-colors"
          >
            <EllipsisHorizontalIcon className="h-4 w-4" />
          </div>
        }
        minConstraints={[200, 160]}
        maxConstraints={[Infinity, 1000]}
        resizeHandles={['s']}
        onResizeStart={() => {
          document
            .querySelector('.dex-chart-wrapper')
            ?.classList.add('pointer-events-none');
        }}
        onResizeStop={handleResize}
      >
        <div className="flex" style={{ height: '100%' }}>
          {/* Changed: removed h-full, added inline style */}
          <div className="flex flex-col flex-1">
            <div className="xxs:hidden lg:flex space-x-1 items-center">
              <TokenNameHeader
                selectedPool={selectedPool}
                contract={contract}
                hotTokens={hotTokens}
              />
            </div>
            <div className="space-y-1 flex flex-col flex-1">
              {selectedPool ? (
                <div className="xxs:h-full" id="tv-chart-wrapper">
                  {contract && (
                    <DexChart
                      contract={contract}
                      poolAddress={selectedPool.poolAddress}
                      setFilters={updateFilters}
                    />
                  )}
                </div>
              ) : (
                <div className="overflow-x-hidden h-full flex-1 min-h-0">
                  <div className="w-full h-full flex-1 min-h-0 relative bg-dex-black-900 flex items-center justify-center">
                    <img
                      src={GeckoTerminalPlaceHolder}
                      className="absolute inset-0 w-full h-full object-cover"
                      style={{ filter: 'blur(5px)' }}
                      alt="Blurred Image"
                    />

                    <div className="absolute xxs:text-dex-white-secondary top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center z-10">
                      <PuzzlePieceIcon className="h-10 w-10" />
                      <p className="text-lg ">
                        {contract.status === 'RUG_PULL'
                          ? 'This token has been rug pulled'
                          : 'This token has not been launched yet'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          {isSwapCompact && (
            <div className="hidden lg:block relative w-[300px] h-full overflow-y-hidden border-tailwind xxs:border-y-0 xxs:border-r-0 border-dex-black-700">
              <SwapsFilterBar isCompact={true} contract={contract} />

              <div className="border-tailwind xxs:border-x-0 xxs:border-b-0 xxs:border-dex-black-700">
                <SwapsTerminal
                  height={chartHeight}
                  isCompact={true}
                  contract={contract}
                />
              </div>

              {paused && (
                <div
                  className="
      absolute bottom-0 left-0 w-full
      bg-dex-black-800/80 backdrop-blur-sm
      border-tailwind xxs:border-x-0 xxs:border-b-0 xxs:border-dex-black-700
      text-white py-2 text-xs
      animate-fadeIn
    "
                >
                  <div className="flex items-center justify-center space-x-1 text-yellow-500">
                    <PauseCircleIcon className="w-4 h-4" />
                    <div>Paused</div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </ResizableBox>
      <OpenTradesFilterProvider>
        <div className="hidden lg:block xxs:mt-2">
          <div className="flex justify-between items-center border-tailwind xxs:border-x-0 xxs:border-t-0 xxs:border-dex-black-700 xxs:pr-2">
            <div className={twMerge('hidden flex xxs:pl-4 xxs:gap-4  w-full')}>
              <AnalyticsButton
                label="Open Trades"
                keyValue={AnalyticsKeys.Trades}
              />
              <AnalyticsButton
                label="History"
                keyValue={AnalyticsKeys.History}
              />
              {!isSwapCompact && (
                <AnalyticsButton
                  label="Swaps"
                  keyValue={AnalyticsKeys.SmartSwaps}
                />
              )}
              <AnalyticsButton
                label="Holders"
                keyValue={AnalyticsKeys.Holders}
                isVisible={contract.generalInfo.chain.key === Chains.Solana}
                extraContent={
                  mainData?.numberOfHolders
                    ? ` (${formatNumberWithSuffix(
                        safeBig(mainData.numberOfHolders),
                        { precision: 0 }
                      )})`
                    : ' (--)'
                }
              />
              <AnalyticsButton
                label="Telegram Calls"
                keyValue={AnalyticsKeys.TelegramCalls}
                isVisible={contract.generalInfo.chain.key !== Chains.Solana}
              />
            </div>

            {(queryParams.activeAnalytics === AnalyticsKeys.Trades ||
              queryParams.activeAnalytics === AnalyticsKeys.History) && (
              <div className="flex space-x-1 items-center">
                <OpenTradesFiltersBar isTerminal={true} />
                {queryParams.activeAnalytics === AnalyticsKeys.History && (
                  <div className="flex items-center xxs:gap-1 xxs:pr-2">
                    <Checkbox
                      id="isFailedTradesHistoryHidden"
                      className="lg:w-4 lg:h-4 xxs:w-5 xxs:h-5"
                      checked={isFailedTradesHistoryHidden}
                      onChange={() =>
                        setIsHistoryTradesFailedHidden(
                          !isFailedTradesHistoryHidden
                        )
                      }
                    />
                    <Checkbox.Label htmlFor="isFailedTradesHistoryHidden">
                      <div className="xxs:text-sm lg:text-xs xxs:text-dex-white-secondary hover:text-dex-white">
                        Hide Failed
                      </div>
                    </Checkbox.Label>
                  </div>
                )}
              </div>
            )}
            {queryParams.activeAnalytics === AnalyticsKeys.SmartSwaps && (
              <SwapsFilterBar contract={contract} />
            )}
            <Button
              type="button"
              onClick={() => {
                if (queryParams.activeAnalytics === AnalyticsKeys.SmartSwaps) {
                  setQueryParams({ activeAnalytics: AnalyticsKeys.Trades });
                }
                setIsSwapCompact(!isSwapCompact);
              }}
              variant="dexNeutral"
              className="hidden lg:block xxs:p-1"
            >
              <div className="flex xxs:space-x-1 items-center ">
                <ViewColumnsIcon
                  className={twMerge('h-4 w-4', isSwapCompact && 'rotate-90')}
                />

                <div className="font-normal normal-case text-nowrap text-xs">
                  Swaps {isSwapCompact ? 'Table' : 'Panel'}
                </div>
              </div>
            </Button>
          </div>

          {contract.generalInfo.chain.key !== Chains.Solana &&
            queryParams.activeAnalytics === AnalyticsKeys.TelegramCalls && (
              <CallsInfo contract={contract} />
            )}

          {queryParams.activeAnalytics === AnalyticsKeys.Trades && (
            <OpenTradesComponent isTerminal={true} />
          )}

          {queryParams.activeAnalytics === AnalyticsKeys.History && (
            <HistoryTradesComponent isTerminal={true} />
          )}

          {!isSwapCompact &&
            queryParams.activeAnalytics === AnalyticsKeys.SmartSwaps && (
              <SwapsTerminal contract={contract} />
            )}

          {queryParams.activeAnalytics === AnalyticsKeys.Holders && (
            <HoldersComponent
              mainData={mainData}
              error={error}
              isLoading={isLoading}
              contract={contract}
            />
          )}
        </div>
      </OpenTradesFilterProvider>
    </div>
  );
}
